import React, { useCallback, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import styled from '@emotion/styled'

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    min-width: 400px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`

const StyledDialogTitle = styled(DialogTitle)`
  padding: 24px 24px 16px;
  font-weight: 600;
  font-size: 1.25rem;
  color: #1a1a1a;
`

const DialogContentStyled = styled(DialogContent)`
  padding: 0 24px 24px;
  color: #4a4a4a;
  
  .MuiTypography-root {
    font-size: 1rem;
    line-height: 1.5;
  }
`

const DialogActionsStyled = styled(DialogActions)`
  padding: 16px 24px;
  border-top: 1px solid #eaeaea;
  gap: 12px;
`

const StyledButton = styled(Button)`
  text-transform: none;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: 6px;
  
  &.MuiButton-contained {
    box-shadow: none;
    &:hover {
      box-shadow: none;
    }
  }
`

type StateType = {
    show: boolean
    confirmed: boolean | null
    title: string
    message: string | JSX.Element
    textConfirm: string
}

export type ResponseConfirmType = {
    confirmContainer: JSX.Element
    onConfirmMessage: (data?: Partial<StateType>) => Promise<boolean>
}

function useConfirm(): ResponseConfirmType {
    const [state, setState] = useState<StateType>({
        show: false,
        confirmed: null,
        title: 'Confirm',
        message: 'Are you sure?',
        textConfirm: 'Confirm',
    })

    const handleClose = useCallback(() => {
        setState(prev => ({ ...prev, show: false, confirmed: false }))
    }, [setState])

    const handleConfirm = useCallback(() => {
        setState(prev => ({ ...prev, show: false, confirmed: true }))
    }, [setState])

    const onConfirmMessage = useCallback(
        async (newData: Partial<StateType> | undefined): Promise<boolean> => {
            setState(prev => ({
                ...prev,
                show: true,
                confirmed: null,
                title: newData?.title ?? prev.title,
                message: newData?.message ?? prev.message,
                textConfirm: newData?.textConfirm ?? prev.textConfirm,
            }))

            return new Promise<boolean>(resolve => {
                const interval = setInterval(() => {
                    setState(prev => {
                        if (prev.confirmed !== null) {
                            clearInterval(interval)
                            resolve(prev.confirmed)
                            return { ...prev, confirmed: null } // Reset the confirmed state for future invocations
                        }
                        return prev
                    })
                }, 100)
            })
        },
        [setState]
    )

    const confirmContainer = (
        <StyledDialog
            open={state.show}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <StyledDialogTitle>{state.title}</StyledDialogTitle>
            <DialogContentStyled>
                {typeof state.message === 'string' ? (
                    <Typography>{state.message}</Typography>
                ) : (
                    state.message
                )}
            </DialogContentStyled>
            <DialogActionsStyled>
                <StyledButton
                    color="inherit"
                    onClick={handleClose}
                    variant="outlined"
                >
                    Cancel
                </StyledButton>
                <StyledButton
                    color="primary"
                    onClick={handleConfirm}
                    variant="contained"
                >
                    {state.textConfirm}
                </StyledButton>
            </DialogActionsStyled>
        </StyledDialog>
    )

    return { confirmContainer, onConfirmMessage }
}

export default useConfirm